import React, { useEffect, useRef } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { useRobotsMeta, isNoindexPath } from '../../hooks/useRobotsMeta';
import { motion } from 'framer-motion';
import { ChevronRight, Clock, Home } from 'lucide-react';
import { ArticleBody, ArticleCta, ArticleFaq, RelatedArticles, SiloBadge } from '../../components/Blog';
import { getArticleBySlug, getRelatedArticles } from '../../data/blog';
import { SILOS } from '../../data/blog/silos';
import { trackBlogArticleView, trackBlogScrollDepth } from '../../utils/analytics';

const SCROLL_THRESHOLDS = [25, 50, 75, 100] as const;

const BlogArticle: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;
  useRobotsMeta(article ? isNoindexPath(`/blog/${article.slug}`) : false);
  const firedThresholds = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!article) return;

    firedThresholds.current = new Set();

    trackBlogArticleView(article.slug, article.h1, article.silo, article.readingMinutes);

    const handleScroll = () => {
      const scrolled = window.scrollY + window.innerHeight;
      const total = document.documentElement.scrollHeight;
      const percent = (scrolled / total) * 100;

      for (const threshold of SCROLL_THRESHOLDS) {
        if (percent >= threshold && !firedThresholds.current.has(threshold)) {
          firedThresholds.current.add(threshold);
          trackBlogScrollDepth(article.slug, threshold);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [article]);

  useEffect(() => {
    if (!article) return;

    document.title = article.metaTitle;

    // Crée la balise si le shell ne la contient pas (index.html ne porte plus de metas de page).
    const setMeta = (attr: 'name' | 'property', key: string, value: string) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', value);
    };

    setMeta('name', 'description', article.metaDescription);
    setMeta('property', 'og:title', article.metaTitle);
    setMeta('property', 'og:description', article.metaDescription);
    setMeta('property', 'og:url', `https://sos-it-services.fr/blog/${article.slug}`);
    setMeta(
      'property',
      'og:image',
      `https://sos-it-services.fr${article.coverImage ?? '/images/heroes/blog-hero.png'}`,
    );

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `https://sos-it-services.fr/blog/${article.slug}`);

    const existingLd = document.getElementById('article-ld-json');
    if (existingLd) existingLd.remove();
    const script = document.createElement('script');
    script.id = 'article-ld-json';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.h1,
      description: article.metaDescription,
      datePublished: article.publishedAt,
      author: { '@type': 'Organization', name: 'SOS IT Services' },
      publisher: {
        '@type': 'Organization',
        name: 'SOS IT Services',
        url: 'https://sos-it-services.fr',
      },
      url: `https://sos-it-services.fr/blog/${article.slug}`,
    });
    document.head.appendChild(script);

    window.scrollTo(0, 0);

    return () => {
      document.getElementById('article-ld-json')?.remove();
    };
  }, [article]);

  if (!article) return <Navigate to="/blog" replace />;

  const silo = SILOS[article.silo];
  const related = getRelatedArticles(article);
  const coverImage = article.coverImage ?? '/images/heroes/blog-hero.webp';
  const coverAlt = article.coverImageAlt ?? `Illustration de l'article : ${article.h1}`;

  return (
    <div className="pt-header md:pt-header-md min-h-screen bg-surface">
      {/* Breadcrumb */}
      <div className="bg-canvas border-b border-line">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <nav className="flex items-center gap-1.5 text-xs text-fg-muted flex-wrap">
            <Link to="/" className="flex min-h-11 items-center gap-1 hover:text-fg transition-colors md:min-h-0">
              <Home className="h-3.5 w-3.5" /> Accueil
            </Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <Link to="/blog" className="inline-flex min-h-11 items-center hover:text-fg transition-colors md:min-h-0">Blog</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <Link to={`/blog/silo/${article.silo}`} className="inline-flex min-h-11 items-center hover:text-fg transition-colors md:min-h-0">
              {silo.title}
            </Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-fg-muted line-clamp-1">{article.h1}</span>
          </nav>
        </div>
      </div>

      {/* Header article */}
      <section className="py-12 md:py-16 bg-gradient-to-b from-canvas to-surface">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
          >
            <div className="flex items-center gap-3 mb-5">
              <SiloBadge silo={article.silo} />
              {article.isPillar && (
                <span className="rounded-full border border-accent/35 bg-accent-strong/15 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-accent-soft">
                  Guide complet
                </span>
              )}
              <span className="flex items-center gap-1.5 text-fg-subtle text-xs ml-auto">
                <Clock className="h-3.5 w-3.5" />
                {article.readingMinutes} min de lecture
              </span>
            </div>

            <h1 className="heading-display mb-4">
              {article.h1}
            </h1>

            <p className="text-fg-muted text-base md:text-lg leading-relaxed">
              {article.excerpt}
            </p>

            <div className="mt-8 overflow-hidden rounded-2xl border border-line shadow-lg shadow-black/20">
              <img src={coverImage} alt={coverAlt} className="h-auto w-full object-cover" />
            </div>
          </motion.div>
        </div>
      </section>

      <div className="h-px bg-gradient-to-r from-transparent via-accent/25 to-transparent" />

      {/* Corps de l'article */}
      <section className="py-12 bg-canvas border-t border-line">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1, ease: 'easeOut' }}
          >
            <ArticleBody content={article.content} />

            <div className="mt-10">
              <ArticleCta variant="inline" articleSlug={article.slug} silo={article.silo} />
            </div>

            <ArticleFaq faq={article.faq} />

            <RelatedArticles articles={related} />

            <div className="mt-12">
              <ArticleCta variant="inline" articleSlug={article.slug} silo={article.silo} />
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BlogArticle;
